import React from 'react';
import {
  BusIcon,
  CalendarClockIcon,
  CheckCircle2Icon,
  Clock3Icon,
  UserRoundIcon,
  UsersRoundIcon,
} from 'lucide-react';
import { ActingDetailQueryResponse } from '@area-restrita/api-client/model/actingDetailQueryResponse';

import { Badge } from '@/components/ui/badge';
import AbortActingAction from '../../components/AbortActingAction';
import ExportBoardingPointsButton from './ExportBoardingPointsButton';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

import {
  formatActingDateTime,
  formatActingDuration,
  getActingStatusLabel,
  getCompletedStepsCount,
} from './actingDetailsPresentation';

interface IProps {
  acting: ActingDetailQueryResponse;
}

const ActingSummary: React.FC<IProps> = ({ acting }) => {
  const completedSteps = getCompletedStepsCount(acting.steps);
  const passengerCountLabel =
    acting.status === 'started' ? 'Lotação atual' : 'Lotação final';
  const details = [
    {
      icon: UsersRoundIcon,
      label: passengerCountLabel,
      value:
        acting.lastPassengerCount === null
          ? 'Não registrada'
          : `${acting.lastPassengerCount} passageiros`,
    },
    {
      icon: BusIcon,
      label: 'Linha',
      value: acting.busline.code,
    },
    {
      icon: UserRoundIcon,
      label: 'Motorista',
      value: acting.startedBy.name ?? 'Não identificado',
    },
    {
      icon: CalendarClockIcon,
      label: 'Início',
      value: formatActingDateTime(acting.startedAt),
    },
    {
      icon: Clock3Icon,
      label:
        acting.finishedAt || acting.status !== 'started'
          ? 'Término'
          : 'Duração até agora',
      value: acting.finishedAt
        ? formatActingDateTime(acting.finishedAt)
        : acting.status === 'started'
          ? formatActingDuration(acting.startedAt, null)
          : 'Não registrado',
    },
    {
      icon: CheckCircle2Icon,
      label: 'Checklist',
      value: acting.usedChecklist
        ? `${completedSteps} de ${acting.totalChecklistSteps} concluídas`
        : 'Não utilizado',
    },
  ];

  return (
    <Card>
      <CardHeader className="gap-3 pb-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1.5">
          <CardTitle>Resumo da execução</CardTitle>
          <CardDescription>
            Informações operacionais registradas durante o percurso
          </CardDescription>
        </div>
        <div className="flex w-fit flex-wrap items-center gap-2">
          <Badge variant="secondary">
            {getActingStatusLabel(acting.status)}
          </Badge>
          <AbortActingAction acting={acting} />
          <ExportBoardingPointsButton acting={acting} />
        </div>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {details.map(({ icon: Icon, label, value }) => (
            <div key={label} className="rounded-lg border px-3 py-3.5">
              <div className="mb-2 flex items-center gap-2">
                <Icon className="size-4 shrink-0 text-muted-foreground" />
                <dt className="truncate text-xs text-muted-foreground">
                  {label}
                </dt>
              </div>
              <dd className="break-words text-sm font-semibold leading-tight">
                {value}
              </dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
};

export default ActingSummary;
